import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios"; // Your configured Axios instance
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

// Types

interface City {
  id: number;
  libelle: string;
}

interface RequestCity {
  libelle: string;
}

interface CityQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

interface PaginatedCityResponse {
  data: City[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

// React Query Keys
export const cityKeys = {
  all: ['cities'] as const,
  list: (page: number, per_page: number, params: Omit<CityQueryParams, 'page' | 'per_page'>) => 
    ['cities', 'list', page, per_page, params] as const,
  detail: (id: number) => ['cities', 'detail', id] as const,
  city: (id: number) => [...cityKeys.all, id] as const,
};

// Fetch all cities with pagination
export const useGetAllCities = (params: CityQueryParams = {}) => {
  const { page = 1, per_page = 10, ...queryParams } = params;
  
  const { data, error, ...res } = useQuery({
    queryKey: cityKeys.list(page, per_page, queryParams),
    queryFn: async (): Promise<PaginatedCityResponse> => {
      const response = await axios.get('/cities/list', { 
        params: { page, per_page, ...queryParams }
      });
      return response.data;
    },
  });

  return { 
    cities: data?.data ?? [], 
    pagination: data ? {
      current_page: data.current_page,
      last_page: data.last_page,
      per_page: data.per_page,
      total: data.total,
      from: data.from,
      to: data.to,
    } : null,
    ...res 
  };
};

// Get one city by ID
export const useGetCity = (id: number) => {
  return useQuery<City>({
    queryKey: cityKeys.city(id),
    queryFn: async () => {
      const response = await axios.get(`/cities/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

// Create a city
export const useCreateCity = (
  onClose?: () => void,
  initcity?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newcity: RequestCity) => {
      const response = await axios.post("/cities/add", newcity);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initcity && initcity();
      
      // Invalidate all city-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: cityKeys.all });
      
      toast.success(t("city.createSuccess", "city created successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("city.unauthorized", "You are not authorized to create cities"));
      } else if (err?.response?.data?.code === "23000") {
        toast.error(t("city.alreadyExists", "city with this name already exists"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("city.createError", "Error creating city"));
      }
    },
  });
};

// Update a city
export const useUpdateCity = (
  cityId: number,
  onClose?: () => void,
  initcity?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updatedcity: RequestCity) => {
      const response = await axios.put(`/cities/${cityId}`, updatedcity);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initcity && initcity();
      
      // Invalidate both the specific city and all city lists
      queryClient.invalidateQueries({ queryKey: cityKeys.detail(cityId) });
      queryClient.invalidateQueries({ queryKey: cityKeys.all });
      
      toast.success(t("city.updateSuccess", "city updated successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("city.unauthorizedUpdate", "You are not authorized to update this city"));
      } else if (err?.response?.data?.code === "23000") {
        toast.error(t("city.nameExists", "A city with this name already exists"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("city.updateError", "Error updating city"));
      }
    },
  });
};

// Delete a city
export const useDeleteCity = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cityId: number) => {
      const response = await axios.delete(`/cities/${cityId}`);
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      
      // Invalidate all city-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: cityKeys.all });
      
      toast.success(t("city.deleteSuccess", "city deleted successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("city.unauthorizedDelete", "You are not authorized to delete this city"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("city.deleteError", "Error deleting city"));
      }
    },
  });
};